import React from "react";
import { Box, Pagination, Tab, Tabs } from "@mui/material";
import { useParams } from "react-router-dom";
import { BreadcrumbAdmin } from "../../../UI/BreadcrumbAdmin";
import { TimeTrials } from "../TimeTrials";
import {
  useApproveTimeTrialSubmissionMutation,
  useGetAdminTimeTrialSubmissionsByTrialQuery,
  useRejectTimeTrialSubmissionMutation,
} from "../../../../redux-toolkit/features/time-trails/timeTrailsApiSlice";
import ProgressBar from "../../../UI/ProgressBar";
import { Error404 } from "../../../UI/404/Error404";
import { NoRecord } from "../../../UI/No record/NoRecord";
import { Search } from "../../../UI/Search/Search";
import "./TaskDetailed.css";
import "./TimeTrialDetailed.css";

const sectionTabSx = {
  textTransform: "capitalize",
  fontWeight: "500",
  fontSize: "15px",
};

const PAGE_SIZE = 10;
const SUPER_ADMIN_TASK_VERIFICATION_PATH =
  "/super-admin/manage-organization/task-verification";
const SUPER_ADMIN_TIME_TRIALS_LIST_PATH =
  `${SUPER_ADMIN_TASK_VERIFICATION_PATH}?tab=time-trials`;

const truncateLabel = (task = "", maxLength = 48) => {
  const text = String(task || "").trim();
  if (!text) return "Time Trial";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}…`;
};

const parseDate = (dateString) => {
  if (!dateString) return null;
  const normalized =
    typeof dateString === "string" &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateString) &&
    !/(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(dateString)
      ? `${dateString}Z`
      : dateString;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatLocalDateTime = (dateString) => {
  const date = parseDate(dateString);
  if (!date) return "--";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
};

const formatDuration = (startDate, endDate) => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  if (!start || !end) return "--";

  const diffInMinutes = Math.floor(
    (end.getTime() - start.getTime()) / (1000 * 60)
  );
  if (diffInMinutes < 0) return "--";

  const days = Math.floor(diffInMinutes / (60 * 24));
  const hours = Math.floor((diffInMinutes % (60 * 24)) / 60);
  const minutes = diffInMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);
  return parts.join(" ");
};

export const TimeTrialDetailed = () => {
  const { timeTrialId } = useParams();
  const [openSection, setOpenSection] = React.useState("PENDING");
  const [searchInput, setSearchInput] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [sectionPages, setSectionPages] = React.useState({
    PENDING: 1,
    APPROVED: 1,
    REJECTED: 1,
  });

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  React.useEffect(() => {
    setSectionPages({
      PENDING: 1,
      APPROVED: 1,
      REJECTED: 1,
    });
  }, [searchQuery, timeTrialId]);

  const { data, isLoading, isFetching, isError, refetch } =
    useGetAdminTimeTrialSubmissionsByTrialQuery(
      {
        timeTrialId,
        status: openSection,
        page: sectionPages[openSection] || 1,
        limit: PAGE_SIZE,
        search: searchQuery,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !timeTrialId,
      }
    );

  const [approveSubmission] = useApproveTimeTrialSubmissionMutation();
  const [rejectSubmission] = useRejectTimeTrialSubmissionMutation();

  const handleApproveSubmission = async (completionId) => {
    try {
      await approveSubmission(completionId).unwrap();
    } catch (error) {
      console.error("Error approving time trial submission:", error);
    }
  };

  const handleRejectSubmission = async (completionId) => {
    try {
      await rejectSubmission(completionId).unwrap();
    } catch (error) {
      console.error("Error rejecting time trial submission:", error);
    }
  };

  const responseData = data?.data || {};
  const timetrial = responseData.timetrial || {};
  const counts = responseData.counts || {
    pending: 0,
    approved: 0,
    rejected: 0,
  };
  const pagination = responseData.pagination || {
    totalPages: 1,
    totalResults: 0,
    page: 1,
  };
  const submissions = (responseData.submissions || []).map((submission) => ({
    ...submission,
    onApprove: () => handleApproveSubmission(submission._id),
    onReject: () => handleRejectSubmission(submission._id),
  }));

  const sectionConfig = [
    { key: "PENDING", label: "Pending", count: counts.pending || 0 },
    { key: "APPROVED", label: "Approved", count: counts.approved || 0 },
    { key: "REJECTED", label: "Rejected", count: counts.rejected || 0 },
  ];

  const activeTabIndex = Math.max(
    0,
    sectionConfig.findIndex((section) => section.key === openSection)
  );

  const base = [
    {
      _id: 1,
      link: SUPER_ADMIN_TIME_TRIALS_LIST_PATH,
      label: "Time Trials",
    },
  ];

  if (isLoading && !data) {
    return <ProgressBar />;
  }

  if (isError) {
    return <Error404 />;
  }

  const hasAnySubmissions =
    (counts.pending || 0) + (counts.approved || 0) + (counts.rejected || 0) > 0;

  const startLabel = formatLocalDateTime(timetrial.startDate);
  const endLabel = formatLocalDateTime(timetrial.endDate);
  const durationLabel = formatDuration(timetrial.startDate, timetrial.endDate);

  return (
    <div className="container">
      <BreadcrumbAdmin
        base={base}
        page={truncateLabel(timetrial.task)}
      />

      <div className="timetrial-detailed__header">
        <div>
          <h2 className="timetrial-detailed__title">
            {startLabel}
            <span className="timetrial-detailed__time-sep"> – </span>
            {endLabel}
          </h2>
          <p className="timetrial-detailed__duration">
            Duration: {durationLabel}
          </p>
          <p className="timetrial-detailed__task">
            {timetrial.task || "No task description"}
          </p>
          {typeof timetrial.points === "number" ? (
            <p className="timetrial-detailed__points">{timetrial.points} points</p>
          ) : null}
        </div>
        <div className="timetrial-detailed__search">
          <Search
            searchVal={searchInput}
            setSearchVal={setSearchInput}
            placehold="Search by name or email"
            mini
          />
        </div>
      </div>

      <div className="taskdetHead__cards">
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Tabs
              value={activeTabIndex}
              onChange={(_event, newValue) =>
                setOpenSection(sectionConfig[newValue]?.key || "PENDING")
              }
              aria-label="time trial status tabs"
            >
              {sectionConfig.map((section) => (
                <Tab
                  key={section.key}
                  label={`${section.label} (${section.count})`}
                  sx={sectionTabSx}
                />
              ))}
            </Tabs>
            <p className="timetrial-detailed__sort-note">
              Sorted by who submitted first
            </p>
          </Box>
        </Box>

        {isFetching ? (
          <div className="timetrial-detailed__loader">
            <ProgressBar />
          </div>
        ) : submissions.length ? (
          <div className="taskdetSection__content">
            <TimeTrials submissions={submissions} hideTaskDescription />
          </div>
        ) : hasAnySubmissions || searchQuery ? (
          <div className="taskdetSection__empty">
            <p>No {openSection.toLowerCase()} submissions found.</p>
            {searchQuery ? (
              <button type="button" onClick={() => refetch()}>
                Refresh
              </button>
            ) : null}
          </div>
        ) : (
          <NoRecord />
        )}

        {(pagination.totalResults || 0) > PAGE_SIZE ? (
          <div className="taskdetSection__pagination">
            <Pagination
              count={Math.max(1, pagination.totalPages || 1)}
              shape="rounded"
              page={sectionPages[openSection] || 1}
              onChange={(_, newPage) =>
                setSectionPages((prev) => ({
                  ...prev,
                  [openSection]: newPage,
                }))
              }
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};
